import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchPublicSurvey,
  PublicSurvey,
  PublicSurveyAnswer,
  submitPublicSurvey,
  SurveyQuestion,
} from '../../services/customerSuccessEngagementApi';

const BARUK_SITE_URL = 'https://baruk.ai';
const BARUK_BLOG_URL = 'https://baruk.ai';

type AnswerState = Record<string, { score: number | null; comment: string }>;

function buildInitialAnswers(questions: SurveyQuestion[]): AnswerState {
  const next: AnswerState = {};
  for (const question of questions) {
    next[question.id] = { score: null, comment: '' };
  }
  return next;
}

function tryCloseWindow(): void {
  window.close();
}

export function PublicSurveyPage() {
  const { token = '' } = useParams();
  const [survey, setSurvey] = useState<PublicSurvey | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<AnswerState>({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void (async () => {
      if (!token) {
        setError('Link inválido.');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await fetchPublicSurvey(token);
        setSurvey(data);
        setAnswers(buildInitialAnswers(data.questions ?? []));
        if (data.alreadyResponded || data.isExpired) {
          setDone(data.alreadyResponded);
        }
      } catch {
        setError('Pesquisa não encontrada ou indisponível.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const questions = useMemo(() => survey?.questions ?? [], [survey?.questions]);
  const canSubmit = useMemo(() => {
    if (!survey || questions.length === 0) return false;
    return questions.every((question) => {
      if (!question.required) return true;
      const current = answers[question.id];
      return current?.score != null;
    });
  }, [answers, questions, survey]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!token || !survey || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const payloadAnswers: PublicSurveyAnswer[] = [];
      for (const question of questions) {
        const current = answers[question.id];
        if (current?.score == null) continue;
        payloadAnswers.push({
          questionId: question.id,
          score: current.score,
          comment: question.allowComment ? current.comment.trim() || undefined : undefined,
        });
      }

      await submitPublicSurvey(token, {
        answers: payloadAnswers,
        comment: survey.allowGeneralComment ? comment.trim() || undefined : undefined,
      });
      setDone(true);
    } catch {
      setError('Não foi possível enviar a avaliação. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  const title =
    survey?.surveyType?.toLowerCase() === 'nps'
      ? 'Pesquisa NPS'
      : 'Avaliação do atendimento';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-slate-100 to-slate-200 px-4 py-10">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
        {survey?.ticketCode ? (
          <p className="mt-1 text-sm text-slate-500">Chamado {survey.ticketCode}</p>
        ) : null}
        {survey?.companyName ? (
          <p className="text-sm text-slate-500">{survey.companyName}</p>
        ) : null}

        {loading ? (
          <p className="mt-6 text-sm text-slate-500">Carregando...</p>
        ) : error && !survey ? (
          <p className="mt-6 text-sm text-red-600">{error}</p>
        ) : done ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-emerald-700">Obrigado! Sua resposta foi registrada.</p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:border-slate-400"
                onClick={tryCloseWindow}
              >
                Fechar janela
              </button>
              <a
                href={BARUK_BLOG_URL}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                target="_blank"
                rel="noopener noreferrer"
              >
                Ir para o blog da Baruk
              </a>
            </div>
            <p className="text-xs text-slate-400">
              Se a janela não fechar, você pode encerrar esta aba do navegador.
            </p>
          </div>
        ) : survey?.isExpired ? (
          <p className="mt-6 text-sm text-amber-700">Este link de pesquisa expirou.</p>
        ) : survey ? (
          <form onSubmit={(event) => void handleSubmit(event)} className="mt-6 space-y-6">
            {questions.map((question) => (
              <QuestionBlock
                key={question.id}
                question={question}
                score={answers[question.id]?.score ?? null}
                comment={answers[question.id]?.comment ?? ''}
                onScoreChange={(score) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [question.id]: {
                      score,
                      comment: prev[question.id]?.comment ?? '',
                    },
                  }))
                }
                onCommentChange={(value) =>
                  setAnswers((prev) => ({
                    ...prev,
                    [question.id]: {
                      score: prev[question.id]?.score ?? null,
                      comment: value,
                    },
                  }))
                }
              />
            ))}

            {survey.allowGeneralComment ? (
              <label className="block text-sm text-slate-700">
                Comentário geral (opcional)
                <textarea
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  rows={3}
                  value={comment}
                  onChange={(event) => setComment(event.target.value)}
                />
              </label>
            ) : null}

            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {submitting ? 'Enviando...' : 'Enviar avaliação'}
            </button>
          </form>
        ) : null}
      </div>

      <footer className="mt-8 max-w-lg text-center text-[11px] leading-relaxed text-slate-400">
        <p>
          © {new Date().getFullYear()} Baruk. Todos os direitos reservados.
        </p>
        <p className="mt-1">
          <a
            href={BARUK_SITE_URL}
            className="underline decoration-slate-300 underline-offset-2 hover:text-slate-500"
            target="_blank"
            rel="noopener noreferrer"
          >
            baruk.ai
          </a>
        </p>
      </footer>
    </div>
  );
}

function QuestionBlock({
  question,
  score,
  comment,
  onScoreChange,
  onCommentChange,
}: {
  question: SurveyQuestion;
  score: number | null;
  comment: string;
  onScoreChange: (score: number) => void;
  onCommentChange: (value: string) => void;
}) {
  const values = Array.from(
    { length: question.max - question.min + 1 },
    (_, index) => question.min + index,
  );
  const type = question.type?.toLowerCase() ?? '';
  const isStars = type === 'stars';
  const isLabeled = type === 'labeledscale';

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-slate-800">
        {question.prompt}
        {question.required ? <span className="text-red-500"> *</span> : null}
      </p>

      {isStars ? (
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <button
              key={value}
              type="button"
              aria-label={`${value} estrela${value === 1 ? '' : 's'}`}
              className={`text-2xl leading-none ${
                score != null && value <= score ? 'text-amber-400' : 'text-slate-300'
              }`}
              onClick={() => onScoreChange(value)}
            >
              ★
            </button>
          ))}
        </div>
      ) : isLabeled ? (
        <div className="space-y-2">
          {values.map((value, index) => (
            <button
              key={value}
              type="button"
              className={`flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${
                score === value
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-800 hover:border-slate-400'
              }`}
              onClick={() => onScoreChange(value)}
            >
              <span>{question.labels[index] || value}</span>
              <span className="opacity-70">{value}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {values.map((value) => (
            <button
              key={value}
              type="button"
              className={`h-10 w-10 rounded-lg border text-sm font-medium ${
                score === value
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-800 hover:border-slate-400'
              }`}
              onClick={() => onScoreChange(value)}
            >
              {value}
            </button>
          ))}
        </div>
      )}

      {question.allowComment ? (
        <label className="block text-sm text-slate-700">
          Comentário (opcional)
          <textarea
            className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            rows={2}
            value={comment}
            onChange={(event) => onCommentChange(event.target.value)}
          />
        </label>
      ) : null}
    </div>
  );
}
